/* Dr.Primal — the capture popup's CRM adapter: Klaviyo.

   Registers as `crm` with `dp-capture.js`. It does, from the browser and with only the
   public site id, what the native Klaviyo signup form does: one call to Klaviyo's client
   subscription endpoint that sets email consent, the list, the signup source and the
   profile properties together. Double opt-in is a setting on the LIST, so it is
   respected here rather than decided.

   Once Klaviyo accepts the subscription, two things follow, fire-and-forget, and never
   hold up or fail the signup:
   - `klaviyo.identify({ email })`, which joins the visitor's onsite browsing and cart
     activity to the new profile. klaviyo.js arrives through the Klaviyo app's onsite
     embed, not through this theme; without it this is skipped.
   - a `Capture Submitted` event carrying the campaign and the offer, so a Klaviyo flow
     can branch per campaign.

   The interface, as the engine calls it. Every method returns a promise and none
   throws synchronously; `subscribe` resolves its outcome instead of rejecting:

     subscribe({ email, properties, source, listId }) → { ok, status, reason? }
         reason: 'invalid_email' (400) · 'http_4xx' · 'http_5xx' · 'network' (offline,
         blocked or timed out; status 0)
     identify({ email })                              → void
     event(name, properties, { email })               → void
     isIdentified()                                   → boolean — false when klaviyo.js
         is absent or does not answer in time, which skips the rule rather than faking it

   Request bodies follow Klaviyo's OpenAPI reference for this revision:
   `create_client_subscription` and `create_client_event`. */
(function () {
  'use strict';

  var KLAVIYO_REVISION = '2026-07-15';          // API revision header; bump only after re-checking both bodies against that revision's reference
  var SUBSCRIBE_URL = 'https://a.klaviyo.com/client/subscriptions'; // client subscription endpoint; company_id is appended from CONFIG
  var EVENTS_URL = 'https://a.klaviyo.com/client/events'; // client event endpoint; company_id is appended from CONFIG
  var CONTENT_TYPE = 'application/vnd.api+json'; // JSON:API media type both endpoints declare in the reference
  var REQUEST_TIMEOUT_MS = 10000;               // a request still pending after this is aborted and reported as 'network'
  var IDENTIFIED_TIMEOUT_MS = 2000;             // how long display rule 2 waits for klaviyo.js to answer before treating the visitor as unknown
  var EVENT_NAME = 'Capture Submitted';         // Klaviyo metric name; any flow triggered by it must be renamed with it
  var CONSENT = 'SUBSCRIBED';                   // the only consent value the client subscription endpoint accepts

  var capture = window.dpCapture;
  if (!capture) {
    console.error('[dp-capture-klaviyo] dp-capture.js must load before this file');
    return;
  }

  function noop() {}

  function endpoint(base) {
    return base + '?company_id=' + encodeURIComponent(capture.config.KLAVIYO_COMPANY_ID);
  }

  function subscriptionBody(request) {
    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: request.source,
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: request.email,
                properties: request.properties || {},
                subscriptions: { email: { marketing: { consent: CONSENT } } },
              },
            },
          },
        },
        relationships: { list: { data: { type: 'list', id: request.listId } } },
      },
    };
  }

  function eventBody(name, properties, profile) {
    return {
      data: {
        type: 'event',
        attributes: {
          properties: properties || {},
          metric: { data: { type: 'metric', attributes: { name: name } } },
          profile: { data: { type: 'profile', attributes: { email: profile.email } } },
        },
      },
    };
  }

  /* HTTP status → outcome. Klaviyo answers 202 on success; any 2xx is taken as one. */
  function outcome(status) {
    if (status >= 200 && status < 300) return { ok: true, status: status };
    if (status === 400) return { ok: false, status: status, reason: 'invalid_email' };
    if (status >= 400 && status < 500) return { ok: false, status: status, reason: 'http_4xx' };
    if (status >= 500) return { ok: false, status: status, reason: 'http_5xx' };
    return { ok: false, status: 0, reason: 'network' };
  }

  /* POSTs a JSON:API body; resolves the HTTP status, or 0 when the request never got
     an answer — offline, blocked by an extension, or cut off at REQUEST_TIMEOUT_MS.
     Never rejects. */
  function post(url, body) {
    return capture
      .fetchWithin(
        url,
        {
          method: 'POST',
          headers: { 'Content-Type': CONTENT_TYPE, revision: KLAVIYO_REVISION },
          body: JSON.stringify(body),
        },
        REQUEST_TIMEOUT_MS
      )
      .then(function (response) {
        return response ? response.status : 0;
      });
  }

  /* klaviyo.js methods return a promise, a plain value, or — when the embed's stub is
     queued and the real script never arrives — a promise that never settles. This
     settles with `fallback` on a throw, a rejection or `ms` of silence. */
  function settleWithin(call, ms, fallback) {
    return new Promise(function (resolve) {
      var timer = window.setTimeout(function () {
        resolve(fallback);
      }, ms);
      function finish(value) {
        window.clearTimeout(timer);
        resolve(value);
      }
      try {
        Promise.resolve(call()).then(finish, function () {
          finish(fallback);
        });
      } catch (error) {
        finish(fallback);
      }
    });
  }

  function klaviyoMethod(name) {
    var klaviyo = window.klaviyo;
    return klaviyo && typeof klaviyo[name] === 'function' ? klaviyo : null;
  }

  function identify(profile) {
    var klaviyo = klaviyoMethod('identify');
    if (!klaviyo) return Promise.resolve();
    return settleWithin(
      function () {
        return klaviyo.identify({ email: profile.email });
      },
      REQUEST_TIMEOUT_MS,
      undefined
    ).then(noop);
  }

  function event(name, properties, profile) {
    var status;
    try {
      status = post(endpoint(EVENTS_URL), eventBody(name, properties, profile));
    } catch (error) {
      return Promise.reject(error);
    }
    return status.then(function (code) {
      // Fire-and-forget, but not silent: nothing else would ever notice a lost event.
      if (!outcome(code).ok) console.error('[dp-capture-klaviyo] "' + name + '" event not accepted', code);
    });
  }

  function subscribe(request) {
    var status;
    try {
      status = post(endpoint(SUBSCRIBE_URL), subscriptionBody(request));
    } catch (error) {
      return Promise.reject(error);
    }
    return status.then(function (code) {
      var result = outcome(code);
      if (result.ok) {
        var profile = { email: request.email };
        var properties = { campaign: capture.config.CAMPAIGN };
        if (capture.config.DISCOUNT_CODE) properties.offer = capture.config.DISCOUNT_CODE;
        identify(profile);
        event(EVENT_NAME, properties, profile).catch(function (error) {
          console.error('[dp-capture-klaviyo]', error);
        });
      }
      return result;
    });
  }

  function isIdentified() {
    var klaviyo = klaviyoMethod('isIdentified');
    if (!klaviyo) return Promise.resolve(false);
    return settleWithin(
      function () {
        return klaviyo.isIdentified();
      },
      IDENTIFIED_TIMEOUT_MS,
      false
    ).then(function (known) {
      return known === true;
    });
  }

  capture.registerAdapter('crm', {
    subscribe: subscribe,
    identify: identify,
    event: event,
    isIdentified: isIdentified,
  });
})();
